import { Drop, Leaf, Lightning } from '@phosphor-icons/react';
import './VehicleEfficiency.css';

/** Значение, которого дилер не указал. Прочерк честнее выдуманного числа. */
const NOT_STATED = '—';

/**
 * Расход, выбросы CO₂ и запас хода — блок раскрытия характеристик автомобиля.
 *
 * ## Зачем отдельный компонент, если рядом есть `NewModelSpecsGrid`
 *
 * Тот — лендинговый: 2×2 без подписей, и он показывает **либо** расход, **либо**
 * CO₂ (первое непустое выигрывает слот). Для раскрытия так нельзя: в ЕС расход
 * и выбросы объявляются **вместе и с подписями** — это то, что покупатель на
 * немецком, люксембургском или кипрском сайте ищет глазами, а дилер обязан
 * показывать (Pkw-EnVKV и аналоги). Поэтому здесь свой компонент, а не проп к
 * лендинговому гриду.
 *
 * ## Почему прочерк, а не скрытая строка
 *
 * Если поле у машины не заполнено, строка всё равно рисуется с `—`. Спрятать её
 * дешевле, но тогда карточка молча выглядит «как будто данных не требуется», и
 * незаполненный инвентарь никто не заметит. Прочерк виден и дилеру, и нам.
 *
 * Если **ни одного** из трёх полей нет — компонент не рендерит ничего: пустой
 * блок из трёх прочерков не сообщает ничего, кроме шума.
 *
 * Витрина ShiftGears передаёт `hideEmpty`: там это страница для покупателя, а
 * не контроль заполненности, и «Electric range: —» у бензинового Picanto
 * читается как дефект (#1335). У дилеров проп не передаётся — поведение то же.
 *
 * ## Клиентская безопасность
 *
 * Компонент едет в бандл виджета вместе с карточкой авто, но **рендерится
 * только когда его включили**: страница передаёт `enabled` из
 * `config.SHOW_EFFICIENCY_DISCLOSURE`, который по умолчанию `false`. У дилера
 * карточка не меняется, пока он сам не включит блок; на витрине он включён
 * (#1330, #1331).
 *
 * @param {object} props
 * @param {object} props.car — объект автомобиля из инвентаря.
 * @param {boolean} [props.enabled] — рендерить ли блок; по умолчанию нет.
 * @param {'rows' | 'inline'} [props.variant] — `rows` для карточки авто,
 *   `inline` для компактного места (например, под ценой в каталоге).
 * @param {(value: string) => string} [props.translate] — перевод значений
 *   инвентаря; данные Autodis лежат по-французски.
 * @param {object} [props.labels] — подписи строк, чтобы компонент не тянул i18n.
 * @param {boolean} [props.hideEmpty] — не рисовать строки без значения.
 */
export const VehicleEfficiency = ({
  car,
  enabled = false,
  variant = 'rows',
  translate = (value) => value,
  // Двоеточие в подписи — не украшение: соседние строки характеристик карточки
  // («Year:», «Fuel:») написаны так же, и блок должен читаться их продолжением.
  labels = {
    consumption: 'Consumption:',
    co2: 'CO₂ emissions:',
    range: 'Electric range:',
  },
  hideEmpty = false,
}) => {
  if (!enabled || !car) return null;

  const allRows = [
    { key: 'consumption', icon: Drop, label: labels.consumption, value: car.consumption },
    { key: 'co2', icon: Leaf, label: labels.co2, value: car.co2Emissions },
    { key: 'range', icon: Lightning, label: labels.range, value: car.range },
  ];

  // Ни одного значения — блок не нужен вовсе.
  if (!allRows.some((row) => row.value)) return null;

  const rows = hideEmpty ? allRows.filter((row) => row.value) : allRows;

  return (
    <dl className={`vehicle-efficiency vehicle-efficiency--${variant}`}>
      {rows.map(({ key, icon: Icon, label, value }) => (
        <div className="vehicle-efficiency__row" key={key}>
          <Icon size={20} className="vehicle-efficiency__icon" aria-hidden="true" />
          <dt className="vehicle-efficiency__label">{label}</dt>
          <dd
            className={`vehicle-efficiency__value${
              value ? '' : ' vehicle-efficiency__value--empty'
            }`}
          >
            {value ? translate(value) : NOT_STATED}
          </dd>
        </div>
      ))}
    </dl>
  );
};

export default VehicleEfficiency;
